import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import TextField from '../stories/general-components/TextField';
import DrawerSide from '../stories/layout-components/DrawerSide';
import nodeAxiosFirebase from '../utils/nodeAxiosFirebase';
import Checkbox from '../stories/general-components/Checkbox';
import { fetchBusinessData } from '../redux/actions-v2/coreAction';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment from 'moment';
import 'moment-timezone';
import { Box, Typography, Chip, IconButton, MenuItem, Select, FormControl, InputLabel, Divider } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import MultiSelect from '../stories/general-components/MultiSelect';
import MultiSelectNode from '../stories/general-components/MultiSelectNode';

// Get all timezones from moment-timezone
const TIMEZONES = moment.tz.names().map(timezone => {
  const offset = moment.tz(timezone).format('Z');
  const city = timezone.split('/').pop().replace(/_/g, ' ');
  const currentTime = moment.tz(timezone).format('HH:mm');
  return {
    value: timezone,
    label: `(GMT${offset}) ${city}`,
    region: timezone.split('/')[0],
    offset: offset,
    currentTime: currentTime
  };
}).sort((a, b) => {
  // Sort by offset first, then by region, then by city
  if (a.offset !== b.offset) {
    return a.offset.localeCompare(b.offset);
  }
  if (a.region !== b.region) {
    return a.region.localeCompare(b.region);
  }
  return a.label.localeCompare(b.label);
});

// Group timezones by region
const TIMEZONE_GROUPS = TIMEZONES.reduce((groups, timezone) => {
  const region = timezone.region;
  if (!groups[region]) {
    groups[region] = [];
  }
  groups[region].push(timezone);
  return groups;
}, {});

const DAYS_OF_WEEK = [
  { value: 'monday', label: 'monday' },
  { value: 'tuesday', label: 'tuesday' },
  { value: 'wednesday', label: 'wednesday' },
  { value: 'thursday', label: 'thursday' },
  { value: 'friday', label: 'friday' },
  { value: 'saturday', label: 'saturday' },
  { value: 'sunday', label: 'sunday' },
];

const DEFAULT_BUSINESS_HOURS = DAYS_OF_WEEK.reduce((acc, day) => {
  acc[day.value] = {
    startTime: '07:00',
    endTime: '17:00',
    isOpen: true
  };
  return acc;
}, {});

const LocationSidebar = ({ closeSidebar, locationId, isDrawerOpen }) => {
  const { t, i18n } = useTranslation();
  const currentLangCode = i18n.language;
  const dispatch = useDispatch();
  const currentPlatform = useSelector(state => state.core.currentPlatform);
  const [selectedLocation, setSelectedLocation] = useState({});
  const [newExcludedPeriod, setNewExcludedPeriod] = useState([null, null]);
  const [selectedItems, setSelectedItems] = useState([])
  const [currentTimes, setCurrentTimes] = useState({});

  const businessPreference = useSelector((state) => state.core.businessData);
  const currentLocation = businessPreference?.locations?.find(
    (location) => location?.id === locationId
  );

  // Get browser's timezone
  const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  useEffect(() => {
    if (currentLocation) {
      // Convert legacy format to new format if needed
      const businessHours = currentLocation.businessHours || DEFAULT_BUSINESS_HOURS;
      if (currentLocation.startTime && currentLocation.endTime && !currentLocation.businessHours) {
        // Convert legacy format to new format
        DAYS_OF_WEEK.forEach(day => {
          businessHours[day.value] = {
            startTime: currentLocation.startTime,
            endTime: currentLocation.endTime,
            isOpen: true
          };
        });
      }

      setSelectedLocation({
        ...currentLocation,
        businessHours,
        timezone: currentLocation.timezone || browserTimezone,
        excludedPeriods: currentLocation.excludedPeriods || [],
        isPublic: currentLocation.isPublic ?? true,
      });
      setSelectedItems(currentLocation?.hooked || [])
    } else {
      setSelectedLocation({
        name: '',
        address: '',
        note: '',
        order: businessPreference?.locations?.length,
        businessHours: DEFAULT_BUSINESS_HOURS,
        timezone: browserTimezone,
        excludedPeriods: [],
        hooked: [],
        isPublic: true,
      });
      setSelectedItems([])
    }
  }, [currentLocation]);

  useEffect(() => {
    const updateTimes = () => {
      const times = {};
      TIMEZONES.forEach(timezone => {
        times[timezone.value] = moment.tz(timezone.value).format('HH:mm');
      });
      setCurrentTimes(times);
    };

    // Update immediately
    updateTimes();

    // Update every minute
    const interval = setInterval(updateTimes, 60000);

    // Cleanup
    return () => clearInterval(interval);
  }, []);

  const handleSave = async () => {
    try {
      // Convert excluded periods to Unix timestamps
      const normalizedExcludedPeriods = selectedLocation.excludedPeriods.map(period => [
        moment(period[0]).unix(),
        moment(period[1]).unix()
      ]).sort((a, b) => a[0] - b[0]);

      await nodeAxiosFirebase({
        t,
        method: 'PATCH',
        url: `business/general`,
        showLoading: true,
        body: {
          type: 'location',
          lang: currentLangCode?.slice(0, 2) || 'fr',
          locationData: {
            ...selectedLocation,
            hooked: selectedItems,
            excludedPeriods: normalizedExcludedPeriods,
          },
        },
      });

      closeSidebar();
      dispatch(fetchBusinessData(businessPreference?.id, t, null, null, currentPlatform));
    } catch (error) {
      console.error('Error saving fields:', error);
    }
  };

  const handleAddExcludedPeriod = () => {
    if (newExcludedPeriod[0] && newExcludedPeriod[1]) {
      setSelectedLocation({
        ...selectedLocation,
        excludedPeriods: [...selectedLocation.excludedPeriods, [...newExcludedPeriod]],
      });
      setNewExcludedPeriod([null, null]);
    }
  };

  const handleRemoveExcludedPeriod = (index) => {
    setSelectedLocation({
      ...selectedLocation,
      excludedPeriods: selectedLocation.excludedPeriods.filter((_, i) => i !== index),
    });
  };

  const handleBusinessHoursChange = (day, field, value) => {
    setSelectedLocation(prev => ({
      ...prev,
      businessHours: {
        ...prev.businessHours,
        [day]: {
          ...prev.businessHours[day],
          [field]: value
        }
      }
    }));
  };

  return (
    <DrawerSide
      title={currentLocation?.name || t('resource')}
      handleDrawerClose={closeSidebar}
      isDrawerOpen={isDrawerOpen}
      handleSave={handleSave}
      isCreation={true}
    >
      <div>
        <TextField
          fullWidth
          type="text"
          label={t('name')}
          value={selectedLocation?.name}
          onChange={(e) =>
            setSelectedLocation({
              ...selectedLocation,
              name: e.target.value,
            })
          }
        />

        <TextField
          fullWidth
          label={t('address')}
          type="geo"
          value={selectedLocation?.address}
          onChange={(e) =>
            setSelectedLocation({
              ...selectedLocation,
              address: e.target.value,
            })
          }
        />

        <div className="col-12">
          <TextField
            fullWidth
            label={t('description')}
            type="text"
            value={selectedLocation?.note}
            onChange={(e) =>
              setSelectedLocation({
                ...selectedLocation,
                note: e.target.value,
              })
            }
          />
        </div>

        <div className="col-12 mt-3">
          <FormControl fullWidth>
            <InputLabel id="timezone-select-label">{t('timezone')}</InputLabel>
            <Select
              labelId="timezone-select-label"
              value={selectedLocation?.timezone || browserTimezone}
              label={t('timezone')}
              onChange={(e) => {
                setSelectedLocation({
                  ...selectedLocation,
                  timezone: e.target.value,
                });
              }}
            >
              {Object.entries(TIMEZONE_GROUPS).map(([region, timezones]) => [
                <MenuItem key={region} disabled>
                  <Typography variant="subtitle2" color="text.secondary">
                    {region}
                  </Typography>
                </MenuItem>,
                ...timezones.map((timezone) => (
                  <MenuItem key={timezone.value} value={timezone.value}>
                    <Box>
                      <Typography variant="body1">
                        {timezone.label}
                        <Typography
                          component="span"
                          variant="body2"
                          color="primary"
                          sx={{ ml: 1 }}
                        >
                          {currentTimes[timezone.value] || timezone.currentTime}
                        </Typography>
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {timezone.value}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))
              ])}
            </Select>
          </FormControl>
        </div>

        <div className="d-flex">
          <div className="col-8" style={{ paddingRight: '20px' }}>
            <TextField
              fullWidth
              label={t('order')}
              type="number"
              value={selectedLocation?.order}
              onChange={(e) =>
                setSelectedLocation({
                  ...selectedLocation,
                  order: e.target.value,
                })
              }
            />
          </div>
          <div className="col-4 mt-1">
            <Checkbox
              value={selectedLocation?.isActive ?? true}
              label={t('isActive')}
              onChange={(e) =>
                setSelectedLocation({
                  ...selectedLocation,
                  isActive: e.target.checked,
                })
              }
              inputProps={{ 'aria-label': 'primary checkbox' }}
            />
          </div>
        </div>

        <div className="col-12 mt-1">
          <Checkbox
            value={selectedLocation?.isPublic ?? true}
            label={t('isPublic')}
            onChange={(e) =>
              setSelectedLocation({
                ...selectedLocation,
                isPublic: e.target.checked,
              })
            }
            inputProps={{ 'aria-label': 'public checkbox' }}
          />
        </div>

        <div className="col-12 mt-3">
          <Typography variant="subtitle1" sx={{ mb: 2 }}>{t('businessHours')}</Typography>
          {DAYS_OF_WEEK.map((day, index) => (
            <div className='row middle-content' key={day.value}>
              <div className="col-3 d-flex align-items-center ">
                <Checkbox
                  value={selectedLocation?.businessHours?.[day.value]?.isOpen ?? true}
                  label={t(day.label)}
                  onChange={(e) => handleBusinessHoursChange(day.value, 'isOpen', e.target.checked)}
                  inputProps={{ 'aria-label': `${day.value} checkbox` }}
                />
              </div>
              {selectedLocation?.businessHours?.[day.value]?.isOpen && (
                <div className="col-9 d-flex">
                  <div className="col-6" style={{ paddingRight: '10px' }}>
                    <LocalizationProvider dateAdapter={AdapterMoment}>
                      <TimePicker
                        label={t('startTime')}
                        value={moment(selectedLocation?.businessHours?.[day.value]?.startTime, 'HH:mm')}
                        onChange={(newValue) =>
                          handleBusinessHoursChange(day.value, 'startTime', newValue.format('HH:mm'))
                        }
                        renderInput={(params) => <TextField {...params} fullWidth />}
                      />
                    </LocalizationProvider>
                  </div>
                  <div className="col-6" style={{ paddingLeft: '10px' }}>
                    <LocalizationProvider dateAdapter={AdapterMoment}>
                      <TimePicker
                        label={t('endTime')}
                        value={moment(selectedLocation?.businessHours?.[day.value]?.endTime, 'HH:mm')}
                        onChange={(newValue) =>
                          handleBusinessHoursChange(day.value, 'endTime', newValue.format('HH:mm'))
                        }
                        renderInput={(params) => <TextField {...params} fullWidth />}
                      />
                    </LocalizationProvider>
                  </div>
                </div>
              )}

            </div>
          ))}
        </div>

        <div className="col-12 mt-3">
          <Typography variant="subtitle1" sx={{ mb: 1 }}>{t('excludedPeriods')}</Typography>
          <div className="d-flex mb-2">
            <div className="col-5" style={{ paddingRight: '10px' }}>
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <DateTimePicker
                  label={t('startDateTime')}
                  value={newExcludedPeriod[0]}
                  onChange={(newValue) =>
                    setNewExcludedPeriod([newValue, newExcludedPeriod[1]])
                  }
                  renderInput={(params) => <TextField {...params} fullWidth />}
                />
              </LocalizationProvider>
            </div>
            <div className="col-5" style={{ paddingLeft: '10px' }}>
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <DateTimePicker
                  label={t('endDateTime')}
                  value={newExcludedPeriod[1]}
                  onChange={(newValue) =>
                    setNewExcludedPeriod([newExcludedPeriod[0], newValue])
                  }
                  renderInput={(params) => <TextField {...params} fullWidth />}
                />
              </LocalizationProvider>
            </div>
            <div className="col-2 d-flex align-items-center">
              <IconButton onClick={handleAddExcludedPeriod} color="primary">
                <AddIcon />
              </IconButton>
            </div>
          </div>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {selectedLocation?.excludedPeriods?.map((period, index) => (
              <Chip
                key={index}
                label={`${moment(period[0]).format('LLL')} - ${moment(period[1]).format('LLL')}`}
                onDelete={() => handleRemoveExcludedPeriod(index)}
                deleteIcon={<DeleteIcon />}
              />
            ))}
          </Box>
        </div>

        <div className="col-12 mt-3 mb-5">
          <Typography variant="subtitle1">{t('hookedElements')}</Typography>

          <MultiSelectNode
            selectionType="services"
            label={t('services')}
            selectedItems={selectedItems}
            setSelectedItems={setSelectedItems}
          />
        </div>
      </div>
    </DrawerSide>
  );
};

export default LocationSidebar;
